<!--
 * @Author: Nemo
 * @Description: 倒计时组件
 * @Date: 2024-07-10
 * @LastEditors: Nemo
-->
<template>
  <div class="countdown-timer" :class="{ 'simplified': isSimplified }" v-if="shouldShowCountdown">
    <!-- 完整模式 -->
    <template v-if="!isSimplified">
      <div class="countdown-header">
        <div class="left-section">
          <img src="@/assets/images/warn.png" alt="warning" class="warning-icon" />
          <span class="countdown-title">{{ $t('message.checkout.tip') }}</span>
        </div>
        <div class="countdown-close" @click="handleClose">
          <span class="el-icon-close"></span>
        </div>
      </div>

      <div class="countdown-body">
        <div class="countdown-content">
          <div class="countdown-text" v-html="$t('message.checkout.timeTip', { time: timeDisplay })">
          </div>
        </div>
      </div>
    </template>

    <!-- 简化模式（单行显示） -->
    <template v-else>
      <div class="simplified-content">
        <img src="@/assets/images/warn.png" alt="warning" class="warning-icon" />
        <div class="simplified-text" v-html="$t('message.checkout.timeTipsShort', { time: timeDisplay })">
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment';

import { convertBeijingTimeToLocal } from '@/utils/tools/format';
export default {
  name: 'OnCountdownTimer',
  props: {
    // 交易创建时间 (格式: yyyy-MM-DD HH:mm:ss)
    createTime: {
      type: [Number, String],
      required: true,
      validator: function(value) {
        // 如果值为空，返回true让required去处理
        if (!value) return true;
        // 验证merchantTxnTime格式是否符合yyyy-MM-DD HH:mm:ss
        return moment(value, 'YYYY-MM-DD HH:mm:ss', true).isValid();
      },
      default: '',
    },
    // 有效时间（单位：分钟）
    effectiveMinutes: {
      type: [Number, String],
      required: true,
      default: '',
    },
    // 是否默认为简化模式
    simplified: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isSimplified: this.simplified,
      currentTime: 0, // 剩余秒数
      timer: null,
      shouldShowCountdown: false,
    };
  },
  computed: {
    // 添加一个计算属性来判断是否应该显示组件
    isValid() {
      return this.createTime &&
             this.effectiveMinutes &&
             this.createTime !== '' &&
             this.effectiveMinutes !== '' &&
             !isNaN(Number(this.effectiveMinutes));
    },
    timeDisplay() {
      // 将剩余秒数转换为时:分:秒格式
      // const hours = Math.floor(this.currentTime / 3600);
      const minutes = Math.floor((this.currentTime % 3600) / 60);
      const seconds = this.currentTime % 60;

      return `<span class="time">${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}</span>`;
    },
  },
  methods: {
    convertBeijingTimeToLocal,
    handleClose() {
      this.isSimplified = true;
      this.$emit('onClose');
    },
    startCountdown() {
      // 添加有效性检查
      if (!this.isValid) {
        this.shouldShowCountdown = false;
        return;
      }
      this.calculateRemainingTime();
      this.timer = setInterval(() => {
        this.calculateRemainingTime();
      }, 1000);
    },
    calculateRemainingTime() {
      try {
        // 添加有效性检查
        if (!this.isValid) {
          this.shouldShowCountdown = false;
          clearInterval(this.timer);
          return;
        }

        // 将北京时间转换为本地时区时间，然后使用moment解析
        const localCreateTime = this.convertBeijingTimeToLocal(this.createTime);
        const startTime = moment(localCreateTime, 'YYYY-MM-DD HH:mm:ss').valueOf();
        // 计算结束时间（交易创建时间 + 有效时间）
        const endTime = startTime + parseInt(this.effectiveMinutes) * 60 * 1000;
        // 计算当前剩余秒数
        const now = moment().valueOf();
        const remainingMs = endTime - now;

        if (remainingMs <= 0) {
          // 倒计时结束，但保持显示状态
          this.currentTime = 0;
          // 修改：确保倒计时结束时也显示
          this.shouldShowCountdown = true;
          clearInterval(this.timer);
          this.$emit('timeEnd');
          return;
        }

        // 计算剩余秒数
        this.currentTime = Math.floor(remainingMs / 1000);

        // 只有在最后3分钟才显示
        const threeMinutesInMs = 3 * 60 * 1000;
        this.shouldShowCountdown = remainingMs <= threeMinutesInMs;
      } catch (error) {
        console.error('计算倒计时出错:', error);
        // 错误处理
        clearInterval(this.timer);
        this.shouldShowCountdown = false;
      }
    },
    resetCountdown(newTime) {
      clearInterval(this.timer);

      if (newTime) {
        // 如果提供了新的时间值，直接作为剩余秒数
        this.currentTime = parseInt(newTime);
        this.shouldShowCountdown = true;

        this.timer = setInterval(() => {
          if (this.currentTime > 0) {
            this.currentTime = this.currentTime - 1;
            // 只有在最后3分钟才显示
            this.shouldShowCountdown = this.currentTime <= 180;
          } else {
            clearInterval(this.timer);
            // 修改：保持显示状态
            this.shouldShowCountdown = true;
            this.$emit('timeEnd');
          }
        }, 1000);
      } else {
        // 重新基于merchantTxnTime和effectiveMinutes计算
        this.startCountdown();
      }
    },
  },
  mounted() {
    // 添加有效性检查
    if (this.isValid) {
      this.startCountdown();
    } else {
      this.shouldShowCountdown = false;
    }
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
};
</script>

<style lang="scss" scoped>
.countdown-timer {
  width: 100%;
  padding: 16px;
  background-color: #FFF9F0;
  box-sizing: border-box;
  border-radius: 2px;
  // 正常模式高度
  height: auto;
  display: flex;
  flex-direction: column;

  // 简化模式样式
  &.simplified {
    height: 56px; // 简化模式固定高度
    padding: 0 16px; // 保持水平内边距
    display: flex;
    align-items: center; // 垂直居中
    flex-direction: row; // 确保水平布局
  }

  .countdown-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;

    .left-section {
      display: flex;
      align-items: center;
    }
  }

  .warning-icon {
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }

  .countdown-title {
    font-family: "Open Sans bold";
    font-weight: 600;
    font-size: 16px;
    color: #1D2129;
  }

  .countdown-body {
    display: flex;
    padding-left: 24px; /* 与标题对齐，24px = 图标宽度(16px) + 右边距(8px) */
  }

  .countdown-content {
    display: flex;
    flex-direction: column;
  }

  .countdown-text {
    font-family: "Open Sans regular";
    font-weight: 400;
    font-size: 14px; // 调整为14px
    color: #1D2129;
    text-align: left;
  }

  .simplified-text {
    font-family: "Open Sans regular";
    font-weight: 400;
    font-size: 14px; // 调整为14px
    color: #1D2129;
    text-align: left;
    transform: translateY(-2px);
  }

  .countdown-close {
    cursor: pointer;

    .el-icon-close {
      font-size: 12px;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      color: #999;
    }
  }

  // 简化模式的单行显示内容
  .simplified-content {
    display: flex;
    align-items: center; // 确保垂直居中
    justify-content: flex-start; // 水平左对齐
    width: 100%;
    height: 100%; // 占满容器高度
  }
}

</style>

<!-- 添加非scoped样式，确保可以应用到v-html渲染的内容 -->
<style lang="scss">
/* 时间显示的样式 - 不使用scoped，确保可以应用到v-html渲染的内容 */
.time {
  font-family: "Open Sans semiBold";
  font-size: 18px;
  color: #FF7D00;
}

/* 只对第一层span应用transform */
.countdown-text > .time {
  transform: translateY(1px);
  display: inline-block;
}

/* 嵌套的span重置样式 */
.countdown-text .time .time {
  transform: none;
  display: inline;
}
/* 只对第一层span应用transform */
.simplified-text > .time {
  transform: translateY(1px);
  display: inline-block;
}

/* 嵌套的span重置样式 */
.simplified-text .time .time {
  transform: none;
  display: inline;
}
</style>
